/*
 * @providesModule StakesSelectorController
 * 
 */

import React from 'react';

import _, {set} from 'lodash';
import GlobalEventEmitter from 'GlobalEventEmitter';
import I18n from 'INTL';
import MatchJoinController from 'MatchJoinController';
import {Navigator} from 'react-native-deprecated-custom-components';
import Prefs from 'Preferences';
import SKZConstants from 'SKZConstants';
import SKZTournament from 'SKZTournament';
import SKZTournamentTemplateSet from 'SKZTournamentTemplateSet';
import TournamentStore from 'TournamentStore';
import ClientPropertyConstants from '../../Utilities/Constants/ClientPropertyConstants';

const CACHED_STAKE_IDS = 'cachedStakeIDs';

class StakesSelectorController {

  temporaryStakesSets: Array<SKZTournamentTemplateSet>;
  temporaryCachedStakesIDs: Object; // { setID: [tournamentIDS]}
  temporarySavedTournaments: Object;
  savedStakesSelectorScreen: React.ReactNode;

  constructor() {
    this.temporaryStakesSets = [];
    this.temporarySavedTournaments = {};
    this.savedStakesSelectorScreen = null;
    this.temporaryCachedStakesIDs = this._getCachedStakes();

    this._addListeners();
  }

  _addListeners() {
    this._removeListeners();
    this.subscriptions = [];
    this.subscriptions.push(GlobalEventEmitter.addListener(SKZConstants.INIT_STAKES_SELECTOR, (templateSets: Array<Object>) => {
      this._initializeStakesSelectorController(templateSets);
    }));
  }

  _removeListeners() {
    if (this.subscriptions) {
      this.subscriptions.forEach((listener: unknown) => {
        if (listener) {
          listener.remove();
        }
      });
      this.subscriptions = [];
    }
  }

  _initializeStakesSelectorController(tournamentTemplateSets: Array<Object>) {
    this.temporaryStakesSets = SKZTournamentTemplateSet.make(tournamentTemplateSets);
    this.temporarySavedTournaments = {};
  }

  _getCachedStakes(): Object {
    let cachedStakes = Prefs.get(CACHED_STAKE_IDS);
    if (typeof cachedStakes == 'string') {
      cachedStakes = JSON.parse(cachedStakes);
      return cachedStakes ? this._cleanCachedStakesForFriends(cachedStakes) : {};
    }
    return {};
  }

  _cleanCachedStakesForFriends(cachedStakes: Object): Object {
    const newCachedStakes = {};
    Object.entries(cachedStakes).forEach(([setID, templateIDs]: [string, Array<string>]) => {
      if (!this._isSetWithIDFriend(setID)) {
        newCachedStakes[setID] = templateIDs;
      }
    });

    return newCachedStakes;
  }

  storeStakesSelectorScreen(screen: React.ReactNode) {
    this.savedStakesSelectorScreen = screen;
  }

  getStoredStakesSelectorScreen(): React.ReactNode {
    return this.savedStakesSelectorScreen;
  }

  getAllSelectedStakeIDsForSet(setID: number): Array<string> {
    // Clears cached tournaments if no longer in the set
    const tournamentsForSet = _.map(this.getTournamentsBySetID(setID), 'id');
    const selectedTournaments = this.temporaryCachedStakesIDs[setID] ? this.temporaryCachedStakesIDs[setID] : [];
    const filteredTournaments = _.filter(selectedTournaments, (tournament: string): boolean => {
      return tournamentsForSet.includes(tournament);
    });
    return filteredTournaments;
  }

  getTournamentsBySetID(setID: number): Array<SKZTournament> {
    const tournaments = _.find(this.temporaryStakesSets, (tempSet: SKZTournamentTemplateSet): boolean => tempSet['id'] == setID);
    return tournaments ? tournaments['tournaments'] : [];
  }

  getSetNameByTournamentId(tournamentId: number) :  string {
    const tournamentsSets = this.getAllSyncTournamentSets();
    let gameMode = '';
    tournamentsSets.forEach((tournamentSet) => {
      if (_.find(tournamentSet.tournaments, (tournament) => { return tournament.id == tournamentId; })) { 
        gameMode = tournamentSet.name;
      } 
    });
    return gameMode; 
  }

  getSetIDByTournamentId(tournamentId: number) :  number | null {
    const tournamentsSets = this.getAllSyncTournamentSets();
    let setID = null;
    tournamentsSets.forEach((tournamentSet) => {
      if (_.find(tournamentSet.tournaments, (tournament) => { return tournament.id == tournamentId; })) { 
        setID = tournamentSet.id;
      } 
    });
    return setID; 
  }

  getSelectedTournamentsBySetID(setID: number): Array<SKZTournament> {
    const unfiltered = this.getTournamentsBySetID(setID);
    const filtered = _.filter(unfiltered, (tournament: SKZTournament): boolean => {
      return this.temporaryCachedStakesIDs[setID]?.includes(tournament['id']);
    });
    return _.map(filtered, (tournament: SKZTournament): SKZTournament => new SKZTournament(tournament));
  }
  
  getSetNameForID(setID: number): string {
    const allSets = this.getAllSyncTournamentSets();
    let templateSet = null;
    allSets.forEach((set: SKZTournamentTemplateSet) => {
      if (!templateSet && setID == set.id) {
        templateSet = set;
      }
    });
    return templateSet?.name || '';
  }

  getTileSubCopyForExperiencedUsers(setID: number): Object {
    const selectedTournamentsArray = this.getSelectedTournamentsBySetID(setID);
    const subCopyObject = {
      cash: '',
      z: '',
      ticketz: '',
    };

    _.forEach(selectedTournamentsArray, (tournament: Object) => {
      tournament = new SKZTournament(tournament);
      if (tournament.isCashTournament()) {
        if (subCopyObject.cash.length > 0) {
          subCopyObject.cash += ', ' + I18n.getCurrencyString(tournament.total_cash_prize, '$', true, false);
        } else {
          subCopyObject.cash += I18n.getCurrencyString(tournament.total_cash_prize, '$', true, false);
        }
      }

      if (tournament.isZTournament()) {
        if (subCopyObject.z.length > 0) {
          subCopyObject.z += ', ' + I18n.getCurrencyString(tournament.total_z_prize, '', true, false);
        } else {
          subCopyObject.z += I18n.getCurrencyString(tournament.total_z_prize, '', true, false);
        }
      }

      if (tournament.isTicketzTournament()) {
        if (subCopyObject.ticketz.length > 0) {
          subCopyObject.ticketz += ', ' + I18n.getCurrencyString(tournament.total_ticketz_prize, '', true, false);
        } else {
          subCopyObject.ticketz += I18n.getCurrencyString(tournament.total_ticketz_prize, '', true, false);
        }
      }
    });

    return subCopyObject;
  }

  _getStakeSetWithID(setID: number): Array<SKZTournamentTemplateSet> {
    return _.find(this.temporaryStakesSets, {id: setID});
  }

  getAllSyncTournamentsForSet(setID: number): Array<SKZTournament> {
    const stakeSet = this._getStakeSetWithID(setID);
    return _.get(stakeSet, 'tournaments', []);
  }

  _isSetWithIDFriend(setID: number): boolean {
    return this._getStakeSetWithID(setID)?.friend;
  }

  getLargestSelectedStakeTournamentInSet(setID: number): SKZTournament {
    const tournaments = this.getSelectedTournamentsBySetID(setID);
    if (!tournaments) {
      return null;
    }
    let largestStakeTournament = tournaments[0];
    _.forEach(tournaments, (tournament: SKZTournament) => {
      largestStakeTournament = SKZTournament.isGreaterThan(tournament, largestStakeTournament) ? tournament : largestStakeTournament;
    });

    return new SKZTournament(largestStakeTournament);
  }

  getCheapestZTournamentInSet(setID: number): SKZTournament | null {
    const zTournaments = this.getAllSyncZTournamentsForSet(setID);

    let cheapestTournament = null;
    zTournaments.forEach((tournament: SKZTournament) => {
      if (!cheapestTournament || cheapestTournament.z_fee > tournament.z_fee) {
        cheapestTournament = tournament;
      }
    });

    return cheapestTournament;
  }

  getCheapestCashTournamentInSet(setID: number): SKZTournament | null {
    const cashTournaments = this.getAllSyncCashTournamentsForSet(setID);

    let cheapestTournament = null;
    cashTournaments.forEach((tournament: SKZTournament) => {
      if (!cheapestTournament || cheapestTournament.cash_fee > tournament.cash_fee) {
        cheapestTournament = tournament;
      }
    });

    return cheapestTournament;
  }

  getAllSyncTournamentSets(): Array<Object> {
    return this.temporaryStakesSets;
  }

  getAllSelectedStakesFromGroupForSet(tournaments: Array<SKZTournament>, setID: number): Array<SKZTournament> {
    const selectedStakes = _.filter(tournaments, (tournament: SKZTournament): SKZTournament => {
      return this.isStakeSelected(setID, tournament.id);
    });
    return selectedStakes;
  }

  getAllSelectedCashStakesForSet(setID: number): Array<SKZTournament> {
    const cashStakes = this.getAllSyncCashTournamentsForSet(setID);
    return this.getAllSelectedStakesFromGroupForSet(cashStakes, setID);
  }

  getAllSelectedTicketzStakesForSet(setID: number): Array<SKZTournament> {
    const ticketzStakes = this.getAllSyncTicketzTournamentsForSet(setID);
    return this.getAllSelectedStakesFromGroupForSet(ticketzStakes, setID);
  }

  getAllSelectedZStakesForSet(setID: number): Array<SKZTournament> {
    const zStakes = this.getAllSyncZTournamentsForSet(setID);
    return this.getAllSelectedStakesFromGroupForSet(zStakes, setID);
  }

  getAllSyncCashTournamentsForSet(setID: number): Array<SKZTournament> {
    const cashTournaments = _.get(this.temporarySavedTournaments[setID], 'cash');
    const cashTournamentsLength = _.get(cashTournaments, 'length', 0);
    if (cashTournamentsLength > 0) {
      return cashTournaments;
    } else {
      const tournaments = this.getAllSyncTournamentsForSet(setID);
      this._createSavedTournamentWithSetID(setID);
      this.temporarySavedTournaments[setID].cash = [];
      _.forEach(tournaments, (tournament: Object) => {
        tournament = new SKZTournament(tournament);
        if (tournament.isCashTournament()) {
          this.temporarySavedTournaments[setID].cash.push(tournament);
        }
      });

      return this.temporarySavedTournaments[setID].cash;
    }
  }

  getAllSyncTicketzTournamentsForSet(setID: number): Array<SKZTournament> {
    const ticketzTournaments = _.get(this.temporarySavedTournaments[setID], 'ticketz');
    const ticketzTournamentsLength = _.get(ticketzTournaments, 'length', 0);
    if (ticketzTournamentsLength > 0) {
      return ticketzTournaments;
    } else {
      const tournaments = this.getAllSyncTournamentsForSet(setID);
      this._createSavedTournamentWithSetID(setID);
      this.temporarySavedTournaments[setID].ticketz = [];
      _.forEach(tournaments, (tournament: Object) => {
        tournament = new SKZTournament(tournament);
        if (tournament.isTicketzTournament()) {
          this.temporarySavedTournaments[setID].ticketz.push(tournament);
        }
      });

      return this.temporarySavedTournaments[setID].ticketz;
    }
  }

  getAllSyncZTournamentsForSet(setID: number): Array<SKZTournament> {
    const zTournaments = _.get(this.temporarySavedTournaments[setID], 'z');
    const zTournamentsLength = _.get(zTournaments, 'length', 0);
    if (zTournamentsLength > 0) {
      return zTournaments;
    } else {
      const tournaments = this.getAllSyncTournamentsForSet(setID);
      this._createSavedTournamentWithSetID(setID);
      this.temporarySavedTournaments[setID].z = [];
      _.forEach(tournaments, (tournament: Object) => {
        tournament = new SKZTournament(tournament);
        if (tournament.isZTournament()) {
          this.temporarySavedTournaments[setID].z.push(tournament);
        }
      });

      return this.temporarySavedTournaments[setID].z;
    }
  }

  setUpTournamentsForSet(setID: number, tournamentTemplate: SKZTournamentTemplateSet) {
    const tournaments = this.getAllSyncTournamentsForSet(setID);
    if (tournaments.length <= 0) {
      this.temporaryStakesSets.push(tournamentTemplate);
    }
  }

  setSelectedStake(setID: number, SKZTournamentID: string) {
    this._createTemporaryCachedStakeWithSetID(setID);

    if (this._isSetWithIDFriend(setID)) {
      this._setSelectedStakesForFriend(setID, SKZTournamentID);
      return;
    }

    const newStakes = JSON.parse(JSON.stringify(this.temporaryCachedStakesIDs));
    if (_.remove(newStakes[setID], (id: number): boolean => id == SKZTournamentID).length == 0) {
      newStakes[setID].push(SKZTournamentID);
    }
    this.temporaryCachedStakesIDs = newStakes;
    // Must be emitted after to ensure updateData() is completed after stakes are set
    GlobalEventEmitter.emit('StakeToggled');
  }

  _setSelectedStakesForFriend(setID: number, SKZTournamentID: string) {
    if (this.temporaryCachedStakesIDs[setID].includes(SKZTournamentID)) {
      this.temporaryCachedStakesIDs[setID] = [];
    } else {
      this.temporaryCachedStakesIDs[setID] = [SKZTournamentID];
    }
    GlobalEventEmitter.emit('StakeToggled');
  }

  isPlayButtonDisabled(setID?: number): boolean {
    if ( ClientPropertyConstants.getFastMatchV2Enabled() ) {
      let selectedStakes = 0;
      for (const id in this.temporaryCachedStakesIDs) {
        selectedStakes += _.size(this.getAllSelectedStakeIDsForSet(id));
      }
      return selectedStakes == 0;
    }

    return _.size(this.temporaryCachedStakesIDs[setID]) == 0;
  }

  resetSelectedStakes() {
    this.temporaryCachedStakesIDs = this._getCachedStakes();
  }

  isStakeSelected(setID: number, SKZTournamentID: string): boolean {
    const cachedStakeIDs = this.temporaryCachedStakesIDs[setID];
    if (cachedStakeIDs) {
      return cachedStakeIDs.includes(SKZTournamentID);
    }
    return false;
  }

  _createTemporaryCachedStakeWithSetID(setID: number) {
    if (this.temporaryCachedStakesIDs[setID] == null) {
      this.temporaryCachedStakesIDs[setID] = [];
    }
  }

  _createSavedTournamentWithSetID(setID: number) {
    if (this.temporarySavedTournaments[setID] == null) {
      this.temporarySavedTournaments[setID] = {};
    }
  }

  setAllStakes() {
    const temporaryCachedStakesIDsJSON = JSON.stringify(this.temporaryCachedStakesIDs);
    Prefs.set({[CACHED_STAKE_IDS]: temporaryCachedStakesIDsJSON});
  }

  isFirstTime(setID: number): boolean {
    return this.getAllSelectedStakeIDsForSet(setID).length === 0;
  }

  clearAllStakes() {
    this.temporaryCachedStakesIDs = {};
    const temporaryCachedStakesIDsJSON = JSON.stringify(this.temporaryCachedStakesIDs);
    Prefs.set({[CACHED_STAKE_IDS]: temporaryCachedStakesIDsJSON});
  }

  isTournamentAStake(setID: number, SKZTournamentID: string): boolean {
    if (!this.temporaryCachedStakesIDs[setID]) {
      return false;
    } else {
      const tournamentIndex = _.indexOf(this.temporaryCachedStakesIDs[setID], SKZTournamentID);
      return tournamentIndex >= 0;
    }
  }

  getTournamentsFromJSON(tournaments: Array<Object>): Array<SKZTournament> {
    if (!tournaments || tournaments.length == 0) {
      return [];
    }
    if (tournaments[0] instanceof SKZTournament) {
      return tournaments;
    }
    return _.map(tournaments, (tournament: Object): SKZTournament => {
      return new SKZTournament(tournament);
    });
  }
  getAllSelectedStakeIDsForAllSets(): Array<string> {
    let stakes = [];
    const sets = this.getAllSyncTournamentSets();
    sets.forEach((set: SKZTournamentTemplateSet) => {
      const selectedStakes = this.getAllSelectedStakeIDsForSet(set.id);
      stakes = stakes.concat(selectedStakes);
    });
    return stakes;
  }

  getAllSelectedStakesForAllSets(): Array<SKZTournament> {
    let stakes = [];
    const sets = this.getAllSyncTournamentSets();
    sets.forEach((set: SKZTournamentTemplateSet) => {
      const selectedStakes = this.getSelectedTournamentsBySetID(set.id);
      stakes = stakes.concat(selectedStakes);
    });
    return stakes;
  }

  getAllSelectedZStakesForAllSets(): Array<SKZTournament> {
    let stakes = [];
    const sets = this.getAllSyncTournamentSets();
    sets.forEach((set: SKZTournamentTemplateSet) => {
      const selectedStakes = this.getAllSelectedZStakesForSet(set.id);
      stakes = stakes.concat(selectedStakes);
    });
    return stakes;
  }

  getAllSelectedCashStakesForAllSets(): Array<SKZTournament> {
    let stakes = [];
    const sets = this.getAllSyncTournamentSets();
    sets.forEach((set: SKZTournamentTemplateSet) => {
      const selectedStakes = this.getAllSelectedCashStakesForSet(set.id);
      stakes = stakes.concat(selectedStakes);
    });
    return stakes;
  }

  // MARK: Tournament Validation

  async validateAndJoinTournaments(setID: number, navigator: Navigator,
    completion: (tournaments: Array<SKZTournament>, setID: number, isValidated: boolean) => {}) {
    const selectedTournamentIds = this.getAllSelectedStakeIDsForSet(setID);
    const tournaments = await this._getTournamentsById(selectedTournamentIds);

    this._validateTournaments(tournaments, navigator, setID, (validated) => {
      this.onValidationCompletion(validated, setID, completion);
    });
  }

  validateSelectedTournaments(tournaments: Array<SKZTournament>, navigator: Navigator, completion: (isValidated: boolean) => void) {
    this._validateTournaments(tournaments.slice(), navigator, 0, completion);
  }

  onValidationCompletion = (isValidated: boolean, setID: number,
    completion: (tournaments: Array<SKZTournament>, setID: number, isValidated: boolean) => {}) => {
    console.debug('_onValidationCompletion completed with isValidated:', isValidated);
    if (isValidated && !this._isSetWithIDFriend(setID)) {
      this.setAllStakes();
    }
    const selectedTournaments = this.getTournamentsFromJSON(this.getSelectedTournamentsBySetID(setID));
    completion(selectedTournaments, setID, isValidated);
  }

  async _getTournamentsById(selectedTournamentIds: Array<string>): Promise<Array<SKZTournament>> {
    const tournaments = [];
    for (const SKZTournamentID of selectedTournamentIds) {
      tournaments.push(await TournamentStore.getTournamentById(SKZTournamentID, false));
    }
    return tournaments;
  }

  _validateTournaments(tournaments: Array<SKZTournament>, navigator: Navigator, setID: number, completion: (isValidated) => {}) {
    this._validateTournament(tournaments.pop(), navigator, (success: boolean) => {
      if (tournaments.length > 0 && success) {
        this._validateTournaments(tournaments, navigator, setID, completion);
      } else {
        completion?.(success);
      }
    });
  }

  _validateTournament(tournament: SKZTournament, navigator: Navigator, callback: (success: boolean) => void) {    
    MatchJoinController.validateTournamentJoin(tournament, false, navigator, (success: boolean) => {
      callback(success);
    });
  }
}

export default new StakesSelectorController();